
import { defineComponent, ref, computed } from "vue"
import { Plus } from "@element-plus/icons-vue"
import { ElMessage } from "element-plus"
import { Cropper } from "vue-advanced-cropper"
import "vue-advanced-cropper/dist/style.css"

export default defineComponent({
  name: "ImageCropUploader",
  components: { Plus, Cropper },
  props: {
    modelValue: {
      type: String,
      default: null
    },
    aspectRatio: {
      type: Number,
      default: undefined
    },
    previewWidth: {
      type: Number,
      default: 178
    },
    previewHeight: {
      type: Number,
      default: 178
    },
    maxFileSize: {
      type: Number,
      default: 2
    },
    outputType: {
      type: String,
      default: "image/jpeg"
    },
    outputQuality: {
      type: Number,
      default: 0.9
    },
    hint: {
      type: String,
      default: ""
    }
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const fileInputRef = ref<HTMLInputElement | null>(null)
    const cropperRef = ref<InstanceType<typeof Cropper> | null>(null)
    const dialogVisible = ref(false)
    const tempImageSrc = ref("")

    const previewStyle = computed(() => ({
      width: props.previewWidth + "px",
      height: props.previewHeight + "px"
    }))

    const placeholderStyle = computed(() => ({
      width: props.previewWidth + "px",
      height: props.previewHeight + "px",
      fontSize: "28px",
      lineHeight: props.previewHeight + "px"
    }))

    const stencilProps = computed(() => {
      if (props.aspectRatio) {
        return { aspectRatio: props.aspectRatio }
      }
      return {}
    })

    const triggerFileInput = () => {
      fileInputRef.value?.click()
    }

    const onFileSelected = (event: Event) => {
      const input = event.target as HTMLInputElement
      const file = input.files?.[0]
      if (!file) return

      // reset input so same file can be re-selected
      input.value = ""

      if (!file.type.startsWith("image/")) {
        ElMessage.error("只能上传图片文件!")
        return
      }
      if (file.size / 1024 / 1024 > props.maxFileSize) {
        ElMessage.error(`图片大小不能超过 ${props.maxFileSize}MB!`)
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        tempImageSrc.value = (e.target?.result as string) || ""
        dialogVisible.value = true
      }
      reader.readAsDataURL(file)
    }

    const onDialogOpened = () => {
      // refresh cropper after dialog animation finishes to fix sizing
      cropperRef.value?.refresh()
    }

    const onConfirmCrop = () => {
      const { canvas } = cropperRef.value?.getResult() || {}
      if (canvas) {
        const base64 = canvas.toDataURL(props.outputType, props.outputQuality)
        emit("update:modelValue", base64)
      }
      dialogVisible.value = false
    }

    return {
      fileInputRef,
      cropperRef,
      dialogVisible,
      tempImageSrc,
      previewStyle,
      placeholderStyle,
      stencilProps,
      triggerFileInput,
      onFileSelected,
      onDialogOpened,
      onConfirmCrop
    }
  }
})
